import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Store scroll positions for specific sections within pages
const sectionScrollPositions = new Map();

export const useSectionScrollMemory = (sectionId) => {
  const location = useLocation();
  const scrollContainerRef = useRef(null);
  const pageKey = location.pathname;
  const sectionKey = sectionId ? `${pageKey}#${sectionId}` : pageKey;

  // Save scroll position when component unmounts or section changes
  useEffect(() => {
    const saveScrollPosition = () => {
      if (scrollContainerRef.current) {
        const scrollTop = scrollContainerRef.current.scrollTop;
        const scrollLeft = scrollContainerRef.current.scrollLeft;
        sectionScrollPositions.set(sectionKey, { scrollTop, scrollLeft });
        console.log(`💾 Saved section scroll position for ${sectionKey}:`, { scrollTop, scrollLeft });
      } else {
        // Fallback to window scroll
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        sectionScrollPositions.set(sectionKey, { scrollTop, scrollLeft });
        console.log(`💾 Saved window section scroll position for ${sectionKey}:`, { scrollTop, scrollLeft });
      }
    };

    // Save scroll position before leaving
    return () => {
      saveScrollPosition();
    };
  }, [sectionKey]);

  // Restore scroll position when component mounts
  useEffect(() => {
    const restoreScrollPosition = () => {
      const savedPosition = sectionScrollPositions.get(sectionKey);
      
      if (savedPosition) {
        console.log(`🔄 Restoring section scroll position for ${sectionKey}:`, savedPosition);
        
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({
            top: savedPosition.scrollTop,
            left: savedPosition.scrollLeft,
            behavior: 'smooth'
          });
        } else {
          // Fallback to window scroll
          window.scrollTo({
            top: savedPosition.scrollTop,
            left: savedPosition.scrollLeft,
            behavior: 'smooth'
          });
        }
      } else {
        console.log(`📍 No saved section scroll position for ${sectionKey}`);
      }
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(restoreScrollPosition, 100);
    
    return () => clearTimeout(timeoutId);
  }, [sectionKey]);

  // Function to manually save current scroll position
  const saveCurrentPosition = () => {
    if (scrollContainerRef.current) {
      const scrollTop = scrollContainerRef.current.scrollTop;
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      sectionScrollPositions.set(sectionKey, { scrollTop, scrollLeft });
      console.log(`💾 Manually saved section scroll position for ${sectionKey}:`, { scrollTop, scrollLeft });
    } else {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      sectionScrollPositions.set(sectionKey, { scrollTop, scrollLeft });
      console.log(`💾 Manually saved window section scroll position for ${sectionKey}:`, { scrollTop, scrollLeft });
    }
  };

  // Function to clear saved position for current section
  const clearSavedPosition = () => {
    sectionScrollPositions.delete(sectionKey);
    console.log(`🗑️ Cleared saved section scroll position for ${sectionKey}`);
  };

  // Function to clear all saved positions for current page
  const clearPagePositions = () => {
    const pagePositions = Array.from(sectionScrollPositions.keys()).filter(key => key.startsWith(pageKey));
    pagePositions.forEach(key => sectionScrollPositions.delete(key));
    console.log(`🗑️ Cleared all saved section scroll positions for ${pageKey}`);
  };

  return {
    scrollContainerRef,
    saveCurrentPosition,
    clearSavedPosition,
    clearPagePositions,
    hasSavedPosition: sectionScrollPositions.has(sectionKey)
  };
};

export default useSectionScrollMemory;
